import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { Button, CircularProgress, Stack, TextField } from '@mui/material';
import Grid from 'material/components/Grid';
import { Add as AddIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { fetchMeeting, patchMeeting, postMeeting } from 'store/actions/meetings';
import { meetingsSelectors } from 'store/selectors/meetings';
import { setSnackbarError, setSnackbarSuccess } from 'store/actions/global';

import ContactsSelection from 'common/containers/ContactsSelection';
import TargetsSelection from 'common/containers/TargetsSelection';
import {
  Backdrop,
  CancelCreatorButton,
  CardField,
  Container,
  DescriptionBar,
} from 'common/components';
import { colors } from 'utils/constants';
import { scrollToTop, showFirstErrorMessage } from 'utils/helpers';

const CardFieldWrapper = styled.div`
  width: 100%;
  margin-top: 30px;
`;

const StyledGrid = styled(Grid)`
  flex: 0 0 100%;
`;

const ButtonCircularProgress = styled(CircularProgress)`
  position: absolute;
  top: 50%;
  left: 50%;
  margin-top: -12px;
  margin-left: -12px;
`;

const StepperWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 90px;
  margin-top: 50px;
  background-color: ${colors.white};
`;

const ButtonCreateWrapper = styled.div`
  position: relative;
  padding-left: 10px;
`;

const CreateMeeting = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { organizationUuid, meetingUuid } = useParams();

  const { isFetching, isPending } = useSelector(state => ({
    isFetching: meetingsSelectors.isFetching(state),
    isPending: meetingsSelectors.isPending(state),
  }));

  const [targets, setTargets] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [errorMessage, setErrorMessage] = useState(false);
  const [occurredOn, setOccurredOn] = useState(dayjs());

  useEffect(() => {
    if (meetingUuid) {
      dispatch(fetchMeeting(meetingUuid)).then(data => {
        setTargets(
          data.targets?.map(target => ({
            ...target,
            office: {
              state: target.state,
              title: target.office,
            },
          }))
        );

        setContacts(data.contacts || []);
        setTitle(data.title || '');
        setDescription(data.description || '');
        setOccurredOn(dayjs(data.occurredOn));
      });
    }
  }, [meetingUuid]);

  const handleSave = () => {
    const meetingDate = occurredOn.format('YYYY-MM-DD');
    const params = {
      contacts: contacts.map(contact => ({ contact: contact.contact || contact.uuid })),
      description,
      occurredOn: meetingDate,
      organization: organizationUuid,
      targets: targets.map(target => ({ term: target.term || target.uuid })),
      title,
    };

    dispatch(meetingUuid ? patchMeeting(meetingUuid, params) : postMeeting(params))
      .then(() => {
        dispatch(setSnackbarSuccess({ message: 'Meeting Saved' }));
        history.push(`/${organizationUuid}/meetings`);
      })
      .catch(error => {
        if (error) {
          scrollToTop();
          setErrorMessage(error);
        }

        if (error && !error.name) {
          dispatch(
            setSnackbarError({
              message: showFirstErrorMessage(error),
            })
          );
        }
      });
  };

  return (
    <Container>
      <Grid container>
        <DescriptionBar name={meetingUuid ? 'Edit Meeting' : 'Create Meeting'} icon={AddIcon}>
          <CancelCreatorButton
            redirectUrl={
              meetingUuid
                ? `/${organizationUuid}/meetings/${meetingUuid}`
                : `/${organizationUuid}/meetings/`
            }
          />
        </DescriptionBar>
      </Grid>
      <Grid container>
        <CardFieldWrapper>
          <CardField flexwrap name="Meeting Info" required>
            <StyledGrid>
              <Stack spacing={3} sx={{ paddingTop: '10px' }}>
                <DatePicker
                  label="Date"
                  value={occurredOn}
                  onChange={date => setOccurredOn(date)}
                  sx={{ maxWidth: '260px' }}
                />
                <TextField
                  fullWidth
                  label="Title"
                  onChange={event => {
                    setTitle(event.target.value);
                    setErrorMessage(null);
                  }}
                  error={!!errorMessage?.name && !!errorMessage?.title[0]}
                  helperText={errorMessage?.name && errorMessage?.title[0]}
                  required
                  type="text"
                  value={title}
                />
                <TextField
                  fullWidth
                  label="Notes"
                  minRows={4}
                  multiline
                  onChange={event => {
                    setDescription(event.target.value);
                    setErrorMessage(null);
                  }}
                  error={!!errorMessage?.name && !!errorMessage?.description[0]}
                  helperText={errorMessage?.name && errorMessage?.description[0]}
                  value={description}
                />
              </Stack>
            </StyledGrid>
          </CardField>
        </CardFieldWrapper>
        <CardFieldWrapper>
          <CardField name="Contacts" flexwrap>
            <StyledGrid>
              <ContactsSelection
                contacts={contacts}
                organizationUuid={organizationUuid}
                setContacts={setContacts}
              />
            </StyledGrid>
          </CardField>
        </CardFieldWrapper>
        <CardFieldWrapper>
          <CardField
            name="Officials"
            flexwrap
            // required
          >
            <TargetsSelection
              setTargets={setTargets}
              targets={targets}
              isCongressGroupSensitive={false}
              hasSavesButton={false}
              organizationUuid={organizationUuid}
            />
          </CardField>
        </CardFieldWrapper>
        <StepperWrapper>
          <Grid container direction="row" justifyContent="flex-end" alignItems="center" size="grow">
            <Grid size="auto">
              <ButtonCreateWrapper>
                <Button
                  color="secondary"
                  disabled={isPending || !targets.length}
                  onClick={handleSave}
                  variant="contained"
                  type="button"
                >
                  {`${meetingUuid ? 'Update' : 'Save'} Meeting`}
                </Button>
                {isPending && <ButtonCircularProgress size={24} />}
              </ButtonCreateWrapper>
            </Grid>
          </Grid>
        </StepperWrapper>
      </Grid>
      <Backdrop isOpen={isFetching} />
    </Container>
  );
};

export default CreateMeeting;
